import { useEffect, useState } from 'react';
import { useMutation } from '@apollo/client';
import { useManagedMutation } from 'utils/managedHooks';
import { REBOOK, OVERRIDE_PRICE } from 'store/mutations';

import { getPendingCancellationExtrasByDirection, isPackage, sortPendingCreationLegsByDirection } from 'utils/booking';
import {
  areLegProductsTrainMarginApplicable,
  areProductFamiliesTrainMarginApplicable,
  calculateMargins,
  getMarginExtraFromExtras
} from 'utils/trainMargins';

async function applyTrainMargins({ data, overridePrice }) {
  const { rebook } = data;

  const isPackageBooking = isPackage(rebook);

  if (!isPackageBooking) {
    return [];
  }

  const trainMarginApplicable = areProductFamiliesTrainMarginApplicable(rebook);

  if (!trainMarginApplicable) {
    return [];
  }

  const { outbound: pendingCancellationOutboundExtras, inbound: pendingCancellationInboundExtras } =
    getPendingCancellationExtrasByDirection(rebook);
  const pendingCancellationOutboundTrainMarginExtra = getMarginExtraFromExtras(pendingCancellationOutboundExtras);
  const pendingCancellationInboundTrainMarginExtra = getMarginExtraFromExtras(pendingCancellationInboundExtras);

  const { outbound, inbound } = sortPendingCreationLegsByDirection(rebook);

  const outboundPendingTrainMarginApplicable = areLegProductsTrainMarginApplicable(outbound);
  const inboundPendingTrainMarginApplicable = areLegProductsTrainMarginApplicable(inbound);

  const outboundOverridePrice = outboundPendingTrainMarginApplicable ? undefined : pendingCancellationOutboundTrainMarginExtra?.price;
  const inboundOverridePrice = inboundPendingTrainMarginApplicable ? undefined : pendingCancellationInboundTrainMarginExtra?.price;

  const items = calculateMargins({ ...rebook, outbound, inbound, outboundOverridePrice, inboundOverridePrice });

  if (!items.length) {
    return;
  }

  const { reference } = rebook;

  await overridePrice({
    variables: {
      reference,
      items
    }
  });
}

const useRebook = () => {
  const [rebook, { loading: loadingRebook, error: errorRebook }] = useMutation(REBOOK);
  const [overridePrice, { loading: loadingOverridePrice, error: errorOverridePrice }] = useManagedMutation(OVERRIDE_PRICE);

  const [handlingMutation, setHandlingMutation] = useState(false);

  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(undefined);

  useEffect(() => {
    const currentlyLoading = loadingRebook || loadingOverridePrice || handlingMutation;
    if (loading !== currentlyLoading) {
      setLoading(currentlyLoading);
    }
  }, [loadingRebook, loadingOverridePrice, handlingMutation, loading, setLoading]);

  useEffect(() => {
    const currentError = errorRebook ?? errorOverridePrice;
    if (error !== currentError) {
      setError(currentError);
    }
  }, [errorRebook, errorOverridePrice, error, setError]);

  const handleMutation = async ({ variables, isPackageBooking }) => {
    setHandlingMutation(true);
    const { data } = await rebook({ variables });
    await applyTrainMargins({ data, isPackageBooking, overridePrice });
    setHandlingMutation(false);
    return data;
  };

  return [handleMutation, { loading, error }];
};

export default useRebook;
